/**
 * P4 — Metrics vocabulary + per-brand KPI/targets API client.
 *
 * Agency employee surface (Bearer = agency `emp_` key). Backend contract
 * (central-api Modules/ClientReporting — consume exactly, do NOT change it):
 *
 *   GET  metrics                                  [kpis.manage OR reports.review]
 *        → { data: MetricDefinition[] }  (global + org-custom)
 *   POST metrics {key,label,…}                    (org custom only) [portal.kpis.manage]
 *   PUT  metrics/{id} {…}                          (org custom only)
 *   DELETE metrics/{id}
 *
 *   GET  shops/{slug}/kpi-targets                 [portal.kpis.manage]
 *        → { data: KpiTarget[] }
 *   POST shops/{slug}/kpi-targets {…}   (UPSERT on the unique tuple
 *        (shop_id, metric_key, platform, period_type, period_start))
 *        → { data: KpiTarget }
 *   PUT    shops/{slug}/kpi-targets/{id} {…}      → { data: KpiTarget }
 *   DELETE shops/{slug}/kpi-targets/{id}
 */
import { request } from './client-reports-http';

// ---------- metrics vocabulary ----------

export type MetricUnit = 'currency' | 'ratio' | 'count' | 'percent' | string;
export type MetricDirection = 'higher_better' | 'lower_better' | string;

export interface MetricDefinition {
  id: string;
  /** null = platform-global metric; non-null = org-custom. */
  organisation_id: string | number | null;
  key: string;
  label: string;
  description: string | null;
  unit: MetricUnit;
  direction: MetricDirection;
  aggregation: string | null;
  platforms: string[] | null;
  synonyms: string[] | null;
}

export interface UpsertMetricBody {
  key: string;
  label: string;
  description?: string | null;
  unit?: MetricUnit;
  direction?: MetricDirection;
  aggregation?: string | null;
  platforms?: string[] | null;
  synonyms?: string[] | null;
}

// ---------- KPI targets ----------

export type TargetPlatform =
  | 'blended'
  | 'meta'
  | 'google'
  | 'tiktok'
  | 'linkedin'
  | 'ga4'
  | string;

export type TargetPeriodType = 'year' | 'quarter' | 'month';
export type TargetType = 'target' | 'cap' | 'floor';

export interface KpiTarget {
  id: string;
  shop_id: number | string;
  metric_key: string;
  platform: TargetPlatform;
  period_type: TargetPeriodType;
  /** YYYY-MM-DD — canonical first day of the period. */
  period_start: string;
  target_value: number;
  target_type: TargetType;
  currency: string | null;
  notes: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface UpsertKpiTargetBody {
  metric_key: string;
  platform: TargetPlatform;
  period_type: TargetPeriodType;
  period_start: string;
  target_value: number;
  target_type: TargetType;
  currency?: string | null;
  notes?: string | null;
}

// ---------- metrics endpoints ----------

export async function listMetrics(): Promise<MetricDefinition[]> {
  const res = await request<{ data?: MetricDefinition[] }>('metrics', {
    method: 'GET',
  });
  return res.data ?? [];
}

export async function createMetric(
  body: UpsertMetricBody
): Promise<MetricDefinition> {
  const res = await request<{ data: MetricDefinition }>('metrics', {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return res.data;
}

export async function updateMetric(
  id: string,
  body: Partial<UpsertMetricBody>
): Promise<MetricDefinition> {
  const res = await request<{ data: MetricDefinition }>(
    `metrics/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(body) }
  );
  return res.data;
}

export async function deleteMetric(id: string): Promise<void> {
  await request<unknown>(`metrics/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// ---------- KPI-target endpoints ----------

export async function listKpiTargets(slug: string): Promise<KpiTarget[]> {
  const res = await request<{ data?: KpiTarget[] }>(
    `shops/${encodeURIComponent(slug)}/kpi-targets`,
    { method: 'GET' }
  );
  return res.data ?? [];
}

/** POST upserts on the unique tuple; returns the created/updated row. */
export async function upsertKpiTarget(
  slug: string,
  body: UpsertKpiTargetBody
): Promise<KpiTarget> {
  const res = await request<{ data: KpiTarget }>(
    `shops/${encodeURIComponent(slug)}/kpi-targets`,
    { method: 'POST', body: JSON.stringify(body) }
  );
  return res.data;
}

export async function updateKpiTarget(
  slug: string,
  id: string,
  body: Partial<UpsertKpiTargetBody>
): Promise<KpiTarget> {
  const res = await request<{ data: KpiTarget }>(
    `shops/${encodeURIComponent(slug)}/kpi-targets/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(body) }
  );
  return res.data;
}

export async function deleteKpiTarget(
  slug: string,
  id: string
): Promise<void> {
  await request<unknown>(
    `shops/${encodeURIComponent(slug)}/kpi-targets/${encodeURIComponent(id)}`,
    { method: 'DELETE' }
  );
}
